import { clickElementById } from "domains/canvas/components/Hotkeys/clickElementById";
import { clickElementByShortcutId } from "domains/canvas/components/Hotkeys/clickElementByShortcutId";
import { getIsUserElement } from "domains/canvas/components/LayerPanel/scenarioElements";
import { exportStoreImage } from "domains/canvas/components/sections/AIPanel/exportStoreImage";
import { maskImageId } from "domains/canvas/components/sections/AIPanel/maskImageId";
import { cropTransparentPixels } from "domains/canvas/components/sections/DrawingTools/mergeImage";
import { PolotnoElement } from "domains/canvas/interfaces";
import { Jimp, JimpImage } from "domains/canvas/utils/jimp";
import {
  AppShortcutCategory,
  ShortcutItem,
} from "domains/shortcuts/components/appShortcuts";
import { ImageElementType } from "polotno/model/image-model";
import { StoreType } from "polotno/model/store";

let store: StoreType;
/** This prevent circular dependency that cause "window is not defined" error */
export const setDefferedStore = (realStore: StoreType) => {
  store = realStore;
};

export async function handleCopy(
  store: StoreType,
  maskElement?: ImageElementType
): Promise<void> {
  if (maskElement) {
    await copyMaskedImage(store, maskElement);
  } else {
    setClipBoard(store.selectedElements.map((el) => el.toJSON()));
  }
}

function handleCut(store: StoreType): void {
  setClipBoard(store.selectedElements.map((el) => el.toJSON()));
  store.deleteElements(store.selectedElements.map((el) => el.id));
}

function handlePaste(store: StoreType): void {
  void store.history.transaction(() => {
    const newElements: string[] = [];
    const clipboardData = getClipBoard();
    clipboardData.forEach((el: PolotnoElement) => {
      if (el.id) delete (el as { id?: string }).id;
      const newElement = store.activePage?.addElement(el);
      if (newElement) {
        newElements.push(newElement.id);
      }
    });
    setClipBoard(clipboardData);
    store.selectElements(newElements);
  });
}
async function copyMaskedImage(
  store: StoreType,
  maskElement: ImageElementType
): Promise<void> {
  // Export the full image
  const fullImage = await exportStoreImage(store);

  // Create a canvas to crop the region
  const img = new Image();
  img.src = fullImage;
  await img.decode();

  const canvas = document.createElement("canvas");
  canvas.width = maskElement.width;
  canvas.height = maskElement.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d context");

  ctx.drawImage(
    img,
    maskElement.x,
    maskElement.y,
    maskElement.width,
    maskElement.height,
    0,
    0,
    maskElement.width,
    maskElement.height
  );

  const croppedImage = canvas.toDataURL();
  const jimpFullImage = await Jimp.read(croppedImage);
  const jimpMaskImage = await Jimp.read(maskElement.src);

  // Ensure mask image matches the exported area dimensions
  jimpMaskImage.resize(maskElement.width, maskElement.height);

  // Normalize the mask
  prepareMaskImage(jimpMaskImage);

  // Apply the mask
  const keptPart = await jimpFullImage
    .mask(jimpMaskImage, 0, 0)
    .getBase64Async(Jimp.MIME_PNG);

  // Crop transparent borders
  const croppedKeptPartData = await cropTransparentPixels(keptPart);

  // Calculate final position relative to the original mask position
  const finalX = maskElement.x + croppedKeptPartData.x;
  const finalY = maskElement.y + croppedKeptPartData.y;

  setClipBoard([
    {
      type: "image",
      x: finalX,
      y: finalY,
      height: croppedKeptPartData.height,
      width: croppedKeptPartData.width,
      src:
        typeof croppedKeptPartData.croppedImage === "string"
          ? croppedKeptPartData.croppedImage
          : await croppedKeptPartData.croppedImage.toDataURL("image/png"),
      opacity: 1,
    },
  ]);
}

function shouldPreventCopyOrCut(store: StoreType): boolean {
  return (
    !store.selectedElements.some(getIsUserElement) &&
    !store.selectedElements.some((el) => el.id === maskImageId)
  );
}

/** Make the partialy transparent pixels opaque and the colored ones to white, keep the fully transparent pixels transparent */

function prepareMaskImage(jimpMaskImage: JimpImage): void {
  jimpMaskImage.scan(
    0,
    0,
    jimpMaskImage.bitmap.width,
    jimpMaskImage.bitmap.height,
    (x, y, idx) => {
      const isTransparent = jimpMaskImage.bitmap.data[idx + 3] === 0;
      if (!isTransparent) {
        jimpMaskImage.bitmap.data[idx + 3] = 255; // Set alpha to fully opaque

        // Set RGB to white
        jimpMaskImage.bitmap.data[idx] = 255; // Red
        jimpMaskImage.bitmap.data[idx + 1] = 255; // Green
        jimpMaskImage.bitmap.data[idx + 2] = 255; // Blue
      }
    }
  );
}

export const canvasShortcuts = {
  selectAll: {
    shortcut: "mod+a",
    description: "Select all",
    action: () => {
      const activePage = store.activePage;
      const selectableElements =
        activePage?.children.filter((el) => el.selectable) || [];
      const selectableElementIds = selectableElements.map((el) => el.id);
      store.selectElements(selectableElementIds);
    },
  },
  undo: {
    shortcut: "mod+z",
    description: "Undo",
    action: () => {
      store.history.undo();
    },
  },
  redo: {
    shortcut: ["mod+shift+z", "mod+y"],
    description: "Redo",
    action: () => {
      store.history.redo();
    },
  },
  copy: {
    shortcut: "mod+c",
    description: "Copy",
    action: () => {
      if (shouldPreventCopyOrCut(store)) {
        return;
      }
      const copy = async () => {
        const maskElement = store.getElementById(maskImageId) as
          | ImageElementType
          | undefined;
        await handleCopy(store, maskElement);
      };
      void copy();
    },
  },
  paste: {
    shortcut: "mod+v",
    description: "Paste",
    action: () => {
      handlePaste(store);
    },
  },
  cut: {
    shortcut: "mod+x",
    description: "Cut",
    action: () => {
      if (shouldPreventCopyOrCut(store)) {
        return;
      }
      handleCut(store);
    },
  },
  delete: {
    shortcut: ["delete", "backspace"],
    description: "Delete",
    action: () => {
      store.deleteElements(store.selectedElements.map((el) => el.id));
    },
  },
  toggleLiveMode: {
    shortcut: "shift+l",
    description: "Toggle Live",
    action: () => clickElementByShortcutId("toggle-live"),
  },
  penTool: {
    shortcut: "s",
    description: "Sketch",
    action: () => clickElementByShortcutId("pen"),
  },
  eraserTool: {
    shortcut: "e",
    description: "Eraser",
    action: () => clickElementByShortcutId("eraser"),
  },
  selectTool: {
    shortcut: "a",
    description: "Select",
    action: () => clickElementByShortcutId("select"),
  },
  maskTool: {
    shortcut: "m",
    description: "Draw a mask",
    action: () => clickElementByShortcutId("aiPen"),
  },
  aiSegmentationTool: {
    shortcut: "shift+m",
    description: "Smart mask",
    action: () => clickElementByShortcutId("aiSegmentation"),
  },
  deleteMask: {
    shortcut: "esc",
    description: "Delete mask",
    action: () => clickElementByShortcutId("delete-mask"),
  },
  aiLassoTool: {
    shortcut: "l",
    description: "Draw a mask (lasso)",
    action: () => clickElementByShortcutId("aiLasso"),
  },
  import: {
    shortcut: "i",
    description: "Import",
    action: () => clickElementByShortcutId("import-element-button"),
    options: {
      preventDefault: true,
    },
  },
  colorPicker: {
    shortcut: "c",
    description: "Color picker",
    action: () => clickElementById("color-picker-button"),
  },
  duplicate: {
    shortcut: "mod+d",
    description: "Duplicate",
    action: () => clickElementByShortcutId("menu-item-duplicate"),
  },
  fit: {
    shortcut: "f",
    description: "Fit",
    action: () => clickElementByShortcutId("menu-item-fit"),
  },
  crop: {
    shortcut: "d",
    description: "Crop",
    action: () => clickElementByShortcutId("menu-item-crop"),
  },
  removeBackground: {
    shortcut: "mod+r",
    description: "Remove Background",
    action: () => clickElementByShortcutId("menu-item-remove-background"),
  },
  enhance: {
    shortcut: "mod+e",
    description: "Enhance",
    action: () => clickElementByShortcutId("menu-item-enhance"),
  },
  flipHorizontal: {
    shortcut: "shift+mod+h",
    description: "Flip horizontal",
    action: () => clickElementByShortcutId("menu-item-flip-horizontal"),
  },
  flipVertical: {
    shortcut: "shift+mod+v",
    description: "Flip vertical",
    action: () => clickElementByShortcutId("menu-item-flip-vertical"),
  },
  resize: {
    shortcut: "shift+r",
    description: "Resize",
    action: () => clickElementByShortcutId("resize-button"),
  },
  saveCanvas: {
    shortcut: "mod+s",
    description: "Save to gallery",
    action: () => clickElementByShortcutId("save-button"),
    options: {
      preventDefault: true,
    },
  },
  shortcutHelp: {
    shortcut: "h",
    description: "Keyboard Shortcuts",
    action: () => {
      clickElementByShortcutId("shortcut-button");
    },
  },
  resizeBrush: {
    shortcut: ["shift+mouse scroll"],
    description: "Resize brush",
    action: () => {},
  },
  aiEraserTool: {
    shortcut: "shift+e",
    description: "Fill",
    action: () => clickElementByShortcutId("aiEraser"),
  },
  clearAllContent: {
    shortcut: ["mod+delete", "mod+backspace"],
    description: "Clear all content",
    action: () => {
      clickElementByShortcutId("canvas-clear-all-content-button");
    },
    options: {
      preventDefault: true,
    },
  },
} satisfies Record<string, AppShortcutCategory | ShortcutItem>;

const setClipBoard = (elements: any[]) => {
  _LOCAL_CLIPBOARD = JSON.parse(JSON.stringify(elements));
  try {
    localStorage.setItem(KEY, JSON.stringify(elements));
  } catch (e) {
    // Handle error if needed
  }
};
const getClipBoard = () => {
  try {
    const data = localStorage.getItem(KEY);
    if (data) return JSON.parse(data);
  } catch (e) {
    // Handle error if needed
  }
  return _LOCAL_CLIPBOARD;
};

let _LOCAL_CLIPBOARD: any[] = [];
const KEY = "polotno_clipboard";
